/**
 * ============================================================================
 * COLLECTION PRODUCT CARDS COMPONENT
 * ============================================================================
 *
 * Enhanced component for Collection product cards with Add-to-Cart functionality
 * Based on the Bestseller implementation but optimized for Collection pages
 *
 * ============================================================================
 */

/**
 * Collection Product Cards Component Class
 */
class CollectionProductCardsComponent {
  constructor() {
    this.sections = document.querySelectorAll('.main-collection');
    this.debug = window.Shopify && window.Shopify.designMode;

    if (this.debug) {
      console.log('CollectionProductCardsComponent initialisiert. Gefundene Sections:', this.sections.length);
    }

    this.init();
  }

  /**
   * Initialize component
   */
  init() {
    // Warte auf DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initSections());
    } else {
      this.initSections();
    }

    // Shopify Section Events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.classList.contains('main-collection')) {
        this.initSection(event.target);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.classList.contains('main-collection')) {
        this.destroySection(event.target);
      }
    });

    // Listen for cart events
    document.addEventListener('cart:add', this.handleCartAdd.bind(this));
  }

  /**
   * Initialize all sections
   */
  initSections() {
    this.sections.forEach(section => this.initSection(section));
  }

  /**
   * Initialize single section
   */
  initSection(section) {
    if (!section) {
      if (this.debug) {
        console.warn('Collection Section ist null oder undefined');
      }
      return;
    }

    const sectionId = section.id || `collection-${Date.now()}`;

    if (this.debug) {
      console.log('Initialisiere Collection Section:', sectionId);
    }

    // Initialize mobile swiper if enabled
    this.initMobileSwiper(section);

    // Initialize product cards
    this.initProductCards(section);
  }

  /**
   * Initialize product cards
   */
  initProductCards(section) {
    const productCards = section.querySelectorAll('[class*="collection-product-card-"]');

    if (this.debug) {
      console.log('Gefundene Produktkarten:', productCards.length);
    }

    productCards.forEach(card => {
      this.initProductCard(card);
    });
  }

  /**
   * Initialize single product card
   */
  initProductCard(card) {
    const form = card.querySelector('collection-product-form');

    if (form) {
      // Add event listeners for better UX
      const button = form.querySelector('.product-card__cta');

      if (button) {
        // Add loading state on click
        button.addEventListener('click', () => {
          this.handleAddToCartClick(button, form);
        });
      }
    }
  }

  /**
   * Handle add to cart button click
   */
  handleAddToCartClick(button, form) {
    if (this.debug) {
      console.log('Add to Cart Button geklickt:', {
        productId: form.dataset.productId,
        button: button
      });
    }

    // Visual feedback
    button.style.transform = 'scale(0.95)';
    setTimeout(() => {
      button.style.transform = '';
    }, 150);
  }

  /**
   * Handle cart add events
   */
  handleCartAdd(event) {
    if (this.debug) {
      console.log('Produkt zum Warenkorb hinzugefügt:', event.detail);
    }

    // Update cart icon or show notification
    this.showAddToCartFeedback(event.detail);
  }

  /**
   * Show add to cart feedback
   */
  showAddToCartFeedback(details) {
    // Optional: Show toast notification or update cart drawer
    if (window.cartDrawer && window.cartDrawer.open) {
      window.cartDrawer.open();
    }
  }

  /**
   * Initialize mobile swiper functionality
   */
  initMobileSwiper(section) {
    const swiperContainer = section.querySelector('.collection-mobile-swiper');
    if (!swiperContainer) return;

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper library not loaded');
      return;
    }

    const sectionId = section.id.replace(/-/g, '_');

    // Initialize Swiper for mobile
    const swiper = new Swiper(swiperContainer.querySelector('.swiper'), {
      slidesPerView: 1,
      spaceBetween: 16,
      loop: true,
      grabCursor: true,
      centeredSlides: true,
      allowTouchMove: true,

      // Navigation
      navigation: {
        nextEl: '.collection-mobile-button-next',
        prevEl: '.collection-mobile-button-prev',
      },

      // Pagination
      pagination: {
        el: '.collection-mobile-pagination',
        clickable: true,
        type: 'bullets',
      },

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Vorheriges Produkt',
        nextSlideMessage: 'Nächstes Produkt',
      }
    });

    if (this.debug) {
      console.log('Mobile Swiper initialisiert für Section:', sectionId);
    }

    // Store swiper instance for cleanup
    section._swiperInstance = swiper;
  }

  /**
   * Destroy section
   */
  destroySection(section) {
    // Cleanup Swiper instance
    if (section._swiperInstance) {
      section._swiperInstance.destroy(true, true);
      delete section._swiperInstance;
    }

    if (this.debug) {
      console.log('Collection Section zerstört:', section.id);
    }
  }

  /**
   * Destroy component
   */
  destroy() {
    // Remove event listeners
    document.removeEventListener('cart:add', this.handleCartAdd.bind(this));

    // Destroy all swiper instances
    this.sections.forEach(section => {
      if (section._swiperInstance) {
        section._swiperInstance.destroy(true, true);
      }
    });
  }

  /**
   * Refresh product cards (useful after AJAX updates)
   */
  refresh() {
    this.sections = document.querySelectorAll('.main-collection');
    this.initSections();
  }
}

/**
 * Initialize Collection Product Cards components
 */
function initCollectionProductCards() {
  // Erstelle eine globale Instanz falls noch nicht vorhanden
  if (!window.collectionProductCardsInstance) {
    window.collectionProductCardsInstance = new CollectionProductCardsComponent();
  }
  return window.collectionProductCardsInstance;
}

/**
 * Auto-initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionProductCards);
} else {
  initCollectionProductCards();
}

/**
 * Make available globally for manual initialization
 */
window.CollectionProductCardsComponent = CollectionProductCardsComponent;
window.initCollectionProductCards = initCollectionProductCards;

/**
 * Register with global theme app if available
 */
if (window.CustomThemeApp && window.CustomThemeApp.registerModule) {
  window.CustomThemeApp.registerModule('collectionProductCards', {
    init: initCollectionProductCards,
    CollectionProductCardsComponent
  });
}

/**
 * Export for ES6 modules
 */
export { CollectionProductCardsComponent, initCollectionProductCards };
